/**
 * Foundation · Timesheet — modello del mese (puro): costruzione giornate, precompilazione,
 * copia, incolla e totali. Nessuna dipendenza dal modulo applicativo.
 */
import { DEFAULT_INTERVAL_COUNT, emptyDay, evaluateDay, copyDayContent, isDayEmpty, normalizeTimeInput } from "./timeCore";

const pad = (n) => String(n).padStart(2, "0");
export const monthDates = (year, month) =>
  Array.from({ length: new Date(year, month, 0).getDate() }, (_, i) => `${year}-${pad(month)}-${pad(i + 1)}`);

const withCount = (day, count) => {
  const intervals = [...(day.intervals || [])];
  while (intervals.length < count) intervals.push({ start: "", end: "" });
  return { justifications: [], note: "", ...day, intervals };
};

/** Giornata dagli orari previsti (profilo); null se non previsti. */
export function plannedDay(date, planned, count = DEFAULT_INTERVAL_COUNT) {
  const ivs = planned?.[date];
  return ivs?.length ? withCount({ date, intervals: ivs.map((iv) => ({ start: iv.start, end: iv.end })) }, count) : null;
}

/** mode: "empty" (A) | "profile" (B). Le giornate della bozza hanno sempre la precedenza. */
export function buildMonthDays({ year, month, draftDays, planned, mode = "empty", count = DEFAULT_INTERVAL_COUNT }) {
  const saved = Object.fromEntries((draftDays || []).map((d) => [d.date, d]));
  return monthDates(year, month).map((date) => {
    if (saved[date]) return withCount(saved[date], count);
    return (mode === "profile" && plannedDay(date, planned, count)) || emptyDay(date, count);
  });
}

export const prefillEmptyDays = (days, planned, count = DEFAULT_INTERVAL_COUNT) =>
  days.map((d) => (isDayEmpty(d) ? plannedDay(d.date, planned, count) || d : d));

/** Copia nel giorno `row` il contenuto del giorno `row - offset` (1 = precedente, 7 = settimana prima). */
export function copyFromOffset(days, row, offset) {
  const src = days[row - offset];
  if (!src || !days[row]) return days;
  return days.map((d, i) => (i === row ? copyDayContent(src, d) : d));
}

/** Incolla una matrice (righe × celle) a partire da (row, col); col = indice E1,U1,E2,U2… */
export function applyPaste(days, row, col, matrix, count = DEFAULT_INTERVAL_COUNT) {
  const next = days.map((d) => ({ ...d, intervals: d.intervals.map((iv) => ({ ...iv })) }));
  matrix.forEach((cells, r) => {
    const day = next[row + r];
    if (!day) return;
    cells.forEach((raw, c) => {
      const k = col + c;
      if (k >= count * 2) return;
      day.intervals[Math.floor(k / 2)][k % 2 ? "end" : "start"] = normalizeTimeInput(raw);
    });
  });
  return next;
}

export const serializeDays = (days) => days.filter((d) => !isDayEmpty(d) || d.note).map((d) => ({
  date: d.date,
  intervals: d.intervals.filter((iv) => (iv.start || "").trim() || (iv.end || "").trim()),
  justifications: (d.justifications || []).map((j) => ({ cause: j.cause || "", minutes: j.minutes ?? null })),
  note: d.note || "",
}));

/** Valutazioni per giorno e totali del mese (minuti). meta[date].expectedMinutes = ore attese. */
export function evaluateMonth(days, meta) {
  const evaluations = {};
  const totals = { worked: 0, pause: 0, justified: 0, theoretical: 0, difference: 0, errors: 0, warnings: 0 };
  days.forEach((d) => {
    const expected = meta[d.date]?.expectedMinutes ?? null;
    const ev = evaluateDay(d, expected);
    evaluations[d.date] = ev;
    totals.worked += ev.workedMinutes;
    totals.pause += ev.pauseMinutes;
    totals.justified += ev.justifiedMinutes;
    totals.theoretical += expected || 0;
    totals.errors += ev.issues.filter((i) => i.level === "error").length;
    totals.warnings += ev.issues.filter((i) => i.level === "warning").length;
  });
  totals.difference = totals.worked + totals.justified - totals.theoretical;
  return { evaluations, totals };
}
